
import { useEffect, useRef, useState } from 'react';
import {
  EmptyState,
  Input,
  Scroller,
  SearchInput,
  Tabs,
  TabsCount,
  TabsList,
  TabsTab,
  Textarea,
  Tooltip,
} from '@/shared/ui';
import {
  ChevronLeft,
  CornerDownLeft,
  Globe,
  Lock,
  Mail,
  Pencil,
  Plus,
  Send,
  Star,
  StickyNote,
  Trash2,
  X,
} from 'lucide-react';
import { apiErrorText,
  responseTemplatesApi,
  type ResponseTemplate,
  type TemplateApplyMode,
  type TemplateCounts,
  type TemplateListParams,
  type TemplateScope,
} from '@/shared/api';
import { useDebouncedValue } from '@/shared/lib/useDebouncedValue';
import { useAuthStore } from '@/features/auth';
import { GroupIcon, ReplyTemplateIcon } from '@/shared/config';
import { cn } from '@/shared/lib/cn';
import styles from './TemplatePanel.module.css';

export interface TemplateContext {
  ticketId: string;
  ticketSubject: string;
  ticketStatus: string;
  ticketPriority: string;
  creatorName: string;
  actorName: string;
  groupId?: string | null;
}

const VARIABLES: { key: string; label: string; hint?: string }[] = [
  { key: 'ticket.subject', label: 'Тема заявки' },
  { key: 'creator.name', label: 'Имя заявителя' },
  {
    key: 'actor.name',
    label: 'Моё имя',
    hint: 'В публичном ответе и письме подставится ваш суточный ник, настоящее имя — только во внутренней заметке',
  },
  { key: 'ticket.status', label: 'Статус' },
  { key: 'ticket.priority', label: 'Приоритет' },
  { key: 'date', label: 'Дата' },
  { key: 'time', label: 'Время' },
];

export function renderTemplateBody(body: string, ctx: TemplateContext): string {
  const now = new Date();
  const vars: Record<string, string> = {
    'ticket.id': ctx.ticketId,
    'ticket.subject': ctx.ticketSubject,
    'ticket.status': ctx.ticketStatus,
    'ticket.priority': ctx.ticketPriority,
    'creator.name': ctx.creatorName,
    'actor.name': ctx.actorName,
    date: now.toLocaleDateString('ru'),
    time: now.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' }),
  };
  return body.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, key: string) => vars[key] ?? m);
}

export interface TemplatePanelProps {
  context: TemplateContext;
  onClose: () => void;
  onInsert: (text: string) => void;
  onApplied?: (result: { mode: TemplateApplyMode; renderedBody: string; messageId?: string }) => void;
  variant?: 'chat' | 'dialog' | 'sheet';
  insertOnly?: boolean;
  className?: string;
}

const PAGE_SIZE = 50;

type PanelMode = { kind: 'list' } | { kind: 'edit'; template: ResponseTemplate | null };
type ListTab = TemplateScope | 'favorites';

const SCOPE_LABEL: Record<TemplateScope, string> = {
  personal: 'Личный',
  group: 'Группа',
  shared: 'Общий',
};

export function TemplatePanel({
  context,
  onClose,
  onInsert,
  onApplied,
  variant = 'chat',
  insertOnly = false,
  className,
}: TemplatePanelProps) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManageShared = hasPermission('manage_shared_templates');
  const userId = useAuthStore((s) => s.user?.id);
  const [templates, setTemplates] = useState<ResponseTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<TemplateCounts | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState('');
  const [listTab, setListTab] = useState<ListTab>('personal');
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [openedId, setOpenedId] = useState<string | null>(null);
  const [mode, setMode] = useState<PanelMode>({ kind: 'list' });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [editName, setEditName] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editScope, setEditScope] = useState<TemplateScope>('personal');
  const [editGroupId, setEditGroupId] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (variant === 'sheet') return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target || panelRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-templates-toggle]')) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, variant]);

  const canEdit = (t: ResponseTemplate): boolean =>
    t.scope === 'shared' ? canManageShared : t.ownerId === userId;
  const groupName = (id: string | null) => groups.find((g) => g.id === id)?.name ?? 'Группа';

  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const tabParams = (tab: ListTab, group: string | null): TemplateListParams | null => {
    if (tab === 'favorites') return { favoritesOnly: true };
    if (tab === 'group') return group ? { scope: 'group', groupId: group } : null;
    return { scope: tab };
  };
  const reqRef = useRef(0);
  const reload = async (append = false) => {
    const req = ++reqRef.current;
    const params = tabParams(listTab, groupId);
    if (append) setLoadingMore(true);
    try {
      const res = await responseTemplatesApi.list({
        ...(params ?? { scope: 'personal' }),
        q: debouncedQuery || undefined,
        limit: PAGE_SIZE,
        offset: append ? templates.length : 0,
      });
      if (req !== reqRef.current) return;
      const page = Array.isArray(res.data) ? res.data : [];
      setTemplates((prev) => (append ? [...prev, ...page] : page));
      setTotal(res.total ?? page.length);
      setCounts(res.counts ?? null);
      setFavoriteIds(new Set(res.favoriteIds ?? []));
      const mine = res.groups ?? [];
      setGroups(mine);
      setGroupId((cur) =>
        cur && mine.some((g) => g.id === cur)
          ? cur
          : (mine.find((g) => g.id === context.groupId)?.id ?? mine[0]?.id ?? null),
      );
    } catch {
      if (req === reqRef.current && !append) setTemplates([]);
    } finally {
      if (req === reqRef.current) {
        setLoaded(true);
        setLoadingMore(false);
      }
    }
  };

  const toggleFavorite = async (id: string) => {
    const wasFavorite = favoriteIds.has(id);
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (wasFavorite) next.delete(id);
      else next.add(id);
      return next;
    });
    try {
      await responseTemplatesApi.toggleFavorite(id);
    } catch {
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (wasFavorite) next.add(id);
        else next.delete(id);
        return next;
      });
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listTab, groupId, debouncedQuery]);

  const filtered = templates;
  const sharedCount = counts?.shared ?? 0;
  const groupCount = (groupId && counts?.groups[groupId]) || 0;
  const favoritesCount = counts?.favorites ?? favoriteIds.size;

  const scopeOptions: TemplateScope[] = [
    'personal',
    ...(groups.length > 0 ? (['group'] as const) : []),
    ...(canManageShared ? (['shared'] as const) : []),
  ];

  const startEdit = (template: ResponseTemplate | null) => {
    setMode({ kind: 'edit', template });
    setEditName(template?.name ?? '');
    setEditBody(template?.body ?? '');
    const fallback: TemplateScope =
      listTab === 'group' && groupId ? 'group' : listTab === 'shared' && canManageShared ? 'shared' : 'personal';
    setEditScope(template?.scope ?? fallback);
    setEditGroupId(template?.groupId ?? groupId);
    setError(null);
  };

  const handleSave = async () => {
    if (!editName.trim() || !editBody.trim()) return;
    setBusy('save');
    setError(null);
    try {
      const scopePatch = {
        scope: editScope,
        groupId: editScope === 'group' ? editGroupId : null,
      };
      if (mode.kind === 'edit' && mode.template) {
        await responseTemplatesApi.update(mode.template.id, {
          name: editName.trim(),
          body: editBody,
          ...scopePatch,
        });
      } else {
        await responseTemplatesApi.create({ name: editName.trim(), body: editBody, ...scopePatch });
      }
      await reload();
      setMode({ kind: 'list' });
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось сохранить шаблон'));
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (id: string) => {
    setBusy(`delete-${id}`);
    setError(null);
    try {
      await responseTemplatesApi.remove(id);
      setOpenedId(null);
      await reload();
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось удалить шаблон'));
    } finally {
      setBusy(null);
    }
  };

  const handleApply = async (template: ResponseTemplate, applyMode: TemplateApplyMode) => {
    setBusy(`apply-${applyMode}`);
    setError(null);
    try {
      const res = await responseTemplatesApi.apply(context.ticketId, template.id, applyMode);
      onApplied?.(res.data);
      onClose();
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось применить шаблон'));
    } finally {
      setBusy(null);
    }
  };

  const insertVariable = (key: string) => {
    const token = `{{${key}}}`;
    const el = bodyRef.current;
    if (!el) {
      setEditBody((b) => b + token);
      return;
    }
    const start = el.selectionStart ?? editBody.length;
    const end = el.selectionEnd ?? editBody.length;
    const next = editBody.slice(0, start) + token + editBody.slice(end);
    setEditBody(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + token.length;
    });
  };

  return (
    <div
      className={cn(
        styles.panel,
        variant === 'dialog' && styles.panelDialog,
        variant === 'sheet' && styles.asSheet,
        className,
      )}
      ref={panelRef}
    >
      {mode.kind === 'list' ? (
        <>
          <div className={styles.head}>
            <span className={styles.headTitle}>Шаблоны ответов</span>
            <button type="button" className={styles.headBtn} onClick={() => startEdit(null)}>
              <Plus size={14} aria-hidden /> Новый
            </button>
            <Tooltip content="Закрыть панель">
              <button
                type="button"
                className={styles.iconBtn}
                onClick={onClose}
                aria-label="Закрыть панель шаблонов"
              >
                <X size={15} aria-hidden />
              </button>
            </Tooltip>
          </div>

          <Tabs value={listTab} onValueChange={(v) => setListTab(v as ListTab)}>
            <TabsList className={styles.tabs}>
              <TabsTab value="personal">Личные</TabsTab>
              {groups.length > 0 && (
                <TabsTab value="group">
                  Группа <TabsCount value={groupCount} />
                </TabsTab>
              )}
              <TabsTab value="shared">
                Общие <TabsCount value={sharedCount} />
              </TabsTab>
              <TabsTab value="favorites">
                Избранные <TabsCount value={favoritesCount} />
              </TabsTab>
            </TabsList>
          </Tabs>

          {listTab === 'group' && groups.length > 1 && (
            <div className={styles.groupPicker} role="radiogroup" aria-label="Группа шаблонов">
              {groups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  role="radio"
                  aria-checked={g.id === groupId}
                  className={cn(styles.scopeBtn, g.id === groupId && styles.scopeActive)}
                  onClick={() => setGroupId(g.id)}
                >
                  {g.name}
                </button>
              ))}
            </div>
          )}

          <SearchInput
            className={styles.searchRow}
            label="Поиск по шаблонам"
            value={query}
            onChange={setQuery}
          />

          {error && <div className={styles.error}>{error}</div>}

          <Scroller className={styles.list} viewportClassName={styles.listViewport}>
            {!loaded && <div className={styles.muted}>Загрузка…</div>}
            {loaded && filtered.length === 0 && (
              <EmptyState
                compact
                Icon={ReplyTemplateIcon}
                title={
                  query.trim()
                    ? 'Ничего не найдено'
                    : listTab === 'group'
                      ? `В группе «${groupName(groupId)}» шаблонов пока нет`
                      : listTab === 'shared'
                        ? 'Общих шаблонов пока нет'
                        : listTab === 'favorites'
                          ? 'Избранных пока нет'
                          : 'Личных шаблонов пока нет'
                }
                hint={
                  query.trim()
                    ? undefined
                    : listTab === 'group'
                      ? 'Шаблон группы видят все её участники, правит — автор.'
                      : listTab === 'favorites'
                        ? 'Отметьте шаблон звёздочкой — он появится здесь.'
                        : listTab === 'personal'
                          ? 'Создайте первый — кнопкой «Новый».'
                          : undefined
                }
              />
            )}
            {filtered.map((t) => {
              const opened = openedId === t.id;
              const rendered = renderTemplateBody(t.body, context);
              return (
                <div key={t.id} className={cn(styles.item, opened && styles.itemOpen)}>
                  <button
                    type="button"
                    className={styles.itemHead}
                    onClick={(e) => {
                      setOpenedId(opened ? null : t.id);
                      if (!opened) {
                        const el = e.currentTarget.parentElement;
                        requestAnimationFrame(() =>
                          el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }),
                        );
                      }
                    }}
                  >
                    <span className={styles.itemName}>
                      {t.name}
                      {t.folder && <span className={styles.itemBadge}>{t.folder}</span>}
                      {t.scope === 'shared' && listTab !== 'shared' && (
                        <span className={styles.itemBadge}>
                          <Globe size={10} aria-hidden /> Общий
                        </span>
                      )}
                      {t.scope === 'group' && listTab !== 'group' && (
                        <span className={styles.itemBadge}>
                          <GroupIcon size={10} aria-hidden /> {groupName(t.groupId)}
                        </span>
                      )}
                      {t.scope !== 'personal' && t.authorName && (
                        <span className={styles.itemAuthor}>
                          {t.ownerId === userId ? 'ваш' : t.authorName}
                        </span>
                      )}
                    </span>
                    { }
                    {!opened && <span className={styles.itemPreview}>{rendered}</span>}
                  </button>
                  { }
                  <Tooltip content={favoriteIds.has(t.id) ? 'Убрать из избранного' : 'В избранное'}>
                    <button
                      type="button"
                      className={styles.favoriteBtn}
                      onClick={() => void toggleFavorite(t.id)}
                      aria-pressed={favoriteIds.has(t.id)}
                      aria-label={
                        favoriteIds.has(t.id)
                          ? `Убрать «${t.name}» из избранного`
                          : `Добавить «${t.name}» в избранное`
                      }
                    >
                      <Star
                        size={14}
                        aria-hidden
                        fill={favoriteIds.has(t.id) ? 'currentColor' : 'none'}
                      />
                    </button>
                  </Tooltip>
                  {opened && (
                    <div className={styles.itemBody}>
                      <div className={styles.preview}>{rendered}</div>
                      <div className={styles.actions}>
                        <button
                          type="button"
                          className={styles.primaryAction}
                          onClick={() => {
                            onInsert(rendered);
                            onClose();
                          }}
                        >
                          <CornerDownLeft size={14} aria-hidden />{' '}
                          {insertOnly ? 'Вставить' : 'В поле ввода'}
                        </button>
                        {!insertOnly && (
                        <>
                        <Tooltip content="Отправить в чат от вашего имени">
                          <button
                            type="button"
                            className={styles.action}
                            disabled={busy === 'apply-public_reply'}
                            onClick={() => void handleApply(t, 'public_reply')}
                          >
                            <Send size={14} aria-hidden /> Ответ
                          </button>
                        </Tooltip>
                        <Tooltip content="Видна только сотрудникам поддержки">
                          <button
                            type="button"
                            className={styles.action}
                            disabled={busy === 'apply-internal_note'}
                            onClick={() => void handleApply(t, 'internal_note')}
                          >
                            <StickyNote size={14} aria-hidden /> Заметка
                          </button>
                        </Tooltip>
                        <Tooltip content="Отправить письмо заявителю">
                          <button
                            type="button"
                            className={styles.action}
                            disabled={busy === 'apply-email'}
                            onClick={() => void handleApply(t, 'email')}
                          >
                            <Mail size={14} aria-hidden /> Письмо
                          </button>
                        </Tooltip>
                        </>
                        )}
                        {canEdit(t) && (
                          <>
                            <span className={styles.actionsSpacer} />
                            <Tooltip content="Изменить шаблон">
                              <button
                                type="button"
                                className={styles.iconBtn}
                                onClick={() => startEdit(t)}
                                aria-label={`Изменить шаблон «${t.name}»`}
                              >
                                <Pencil size={14} aria-hidden />
                              </button>
                            </Tooltip>
                            <Tooltip content="Удалить шаблон">
                              <button
                                type="button"
                                className={cn(styles.iconBtn, styles.danger)}
                                disabled={busy === `delete-${t.id}`}
                                onClick={() => void handleDelete(t.id)}
                                aria-label={`Удалить шаблон «${t.name}»`}
                              >
                                <Trash2 size={14} aria-hidden />
                              </button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {loaded && templates.length < total && (
              <button
                type="button"
                className={styles.loadMore}
                disabled={loadingMore}
                onClick={() => void reload(true)}
              >
                {loadingMore ? 'Загрузка…' : `Показать ещё ${Math.min(PAGE_SIZE, total - templates.length)}`}
              </button>
            )}
          </Scroller>
        </>
      ) : (
        <>
          <div className={styles.head}>
            <Tooltip content="К списку шаблонов">
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setMode({ kind: 'list' })}
                aria-label="Вернуться к списку шаблонов"
              >
                <ChevronLeft size={16} aria-hidden />
              </button>
            </Tooltip>
            <span className={styles.headTitle}>
              {mode.template ? 'Изменить шаблон' : 'Новый шаблон'}
            </span>
            <Tooltip content="Закрыть панель">
              <button
                type="button"
                className={styles.iconBtn}
                onClick={onClose}
                aria-label="Закрыть панель шаблонов"
              >
                <X size={15} aria-hidden />
              </button>
            </Tooltip>
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <Scroller className={styles.editor} viewportClassName={styles.editorViewport}>
            {scopeOptions.length > 1 && (
              <div className={styles.scopeToggle} role="radiogroup" aria-label="Кому виден шаблон">
                {scopeOptions.map((sc) => (
                  <button
                    key={sc}
                    type="button"
                    role="radio"
                    aria-checked={editScope === sc}
                    className={cn(styles.scopeBtn, editScope === sc && styles.scopeActive)}
                    onClick={() => setEditScope(sc)}
                  >
                    {sc === 'personal' ? (
                      <Lock size={12} aria-hidden />
                    ) : sc === 'group' ? (
                      <GroupIcon size={12} aria-hidden />
                    ) : (
                      <Globe size={12} aria-hidden />
                    )}{' '}
                    {SCOPE_LABEL[sc]}
                  </button>
                ))}
              </div>
            )}
            {editScope === 'group' && groups.length > 1 && (
              <div className={styles.scopeToggle} role="radiogroup" aria-label="Группа шаблона">
                {groups.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    role="radio"
                    aria-checked={editGroupId === g.id}
                    className={cn(styles.scopeBtn, editGroupId === g.id && styles.scopeActive)}
                    onClick={() => setEditGroupId(g.id)}
                  >
                    {g.name}
                  </button>
                ))}
              </div>
            )}
            {editScope === 'group' && (
              <p className={styles.scopeHint}>
                Увидят все участники группы «{groupName(editGroupId)}», править сможете только вы.
              </p>
            )}
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Название (например, «Приветствие»)"
              autoFocus
            />
            <Textarea
              ref={bodyRef}
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              placeholder="Здравствуйте, {{creator.name}}! По заявке «{{ticket.subject}}»…"
              rows={6}
            />
            <div className={styles.varsRow}>
              {VARIABLES.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className={styles.varChip}
                  onClick={() => insertVariable(v.key)}
                  title={v.hint ? `Вставить {{${v.key}}}. ${v.hint}` : `Вставить {{${v.key}}}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
            {editBody.trim() && (
              <div className={styles.previewBox}>
                <span className={styles.previewLabel}>Предпросмотр</span>
                <div className={styles.preview}>
                  {renderTemplateBody(editBody, context)}
                </div>
              </div>
            )}
            <div className={styles.editorActions}>
              <button
                type="button"
                className={styles.primaryAction}
                onClick={() => void handleSave()}
                disabled={busy === 'save' || !editName.trim() || !editBody.trim()}
              >
                {mode.template ? 'Сохранить' : 'Создать шаблон'}
              </button>
              <button type="button" className={styles.action} onClick={() => setMode({ kind: 'list' })}>
                Отмена
              </button>
            </div>
          </Scroller>
        </>
      )}
    </div>
  );
}
